import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SessionService } from '@src/app/shared/services/session.service';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { DigitalPassportService, ItemDigitalPassportRequest, ItemDigitalPassportResponse, WarrantyDigitalPassportRequest, WarrantyDigitalPassportResponse, WarrantyMinimalResponse } from '@src/app/shared/backend/clients';
import { LoginNavigationService } from '@src/app/shared/services/loginNavigation.service';
import { TranslateService } from '@ngx-translate/core';
import { NoticePlacement } from '@src/app/shared/enums/NoticePlacement.enum';

@Component({
    selector: 'app-digital-passport',
    templateUrl: './digital-passport.component.html',
    styleUrls: ['./digital-passport.component.scss'],
    host: {
        class: 'app-digital-passport'
    },
    standalone: false
})
export class DigitalPassportComponent implements OnInit {

  warrantyData: WarrantyDigitalPassportResponse = {};
  itemData: ItemDigitalPassportResponse = {}
  noWarrantyFound: boolean = false;
  loading = false;
  error: string | null = null;
  displayPasscodeDialog: boolean = false;
  headerText: string | null = '';
  footerText: string | null = '';

  warrantyList: WarrantyMinimalResponse[] = [];
  productInfo: any = null;

  constructor(
    public permissionService: PermissionService,
    private sessionService: SessionService,
    private digitalPassportService: DigitalPassportService,
    private route: ActivatedRoute,
    private router: Router,
    private loginNavigationService: LoginNavigationService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.initializeDigitalPassport();
    // this.displayPasscodeDialog = true;// mock
  }

  async initializeDigitalPassport(): Promise<void> {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.error = null;
    this.noWarrantyFound = false;

    try {
      const companyIdentifier = this.route.parent?.snapshot.paramMap.get('companyIdentifier');

      if (!companyIdentifier) {
        throw new Error('Missing required route parameters');
      }

      // 1. check if passcode is required for this company
      this.displayPasscodeDialog = await firstValueFrom(this.digitalPassportService.apiDigitalPassportValueGet(companyIdentifier));

    } catch (error) {
      console.error('Error fetching digital passport settings:', error);
      this.error = this.translate.instant('ERRORS.GENERIC_ERROR');
      return;
    } finally {
      this.loading = false;
    }

    if (!this.displayPasscodeDialog) {
      await this.getWarrantyData();
    }
  }

  async getWarrantyData(securityPasscode: string | null = null): Promise<void> {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.error = null;
    this.noWarrantyFound = false;

    try {
      const type = this.route.parent?.snapshot.url[2].path;
      const companyIdentifier = this.route.parent?.snapshot.paramMap.get('companyIdentifier');

      if(type === 'w'){
        const warrantyCode = this.route.snapshot.paramMap.get('warrantyCode');

        if (!companyIdentifier || !warrantyCode) {
          throw new Error('Missing required route parameters');
        }

        const payload: WarrantyDigitalPassportRequest = {
          warrantyCode: warrantyCode,
          companyIdentifier: companyIdentifier,
          securityPasscode: securityPasscode
        };

        // 2. fetch warranty with passcode if required
        this.warrantyData = await firstValueFrom(this.digitalPassportService.apiDigitalPassportWarrantyPost(payload));

        this.productInfo = {
          productName: this.warrantyData.productName,
          serialNumber: this.warrantyData.serialNumber,
          sku: this.warrantyData.sku,
          productImageUrl: this.warrantyData.productImageUrl,
          purchaseDate: this.warrantyData.purchaseDate,
          ctaUrl: this.warrantyData.ctaUrl
        };
        const warranty : WarrantyMinimalResponse ={
          activationDate: this.warrantyData.activationDate,
          code: this.warrantyData.code,
          endDate: this.warrantyData.endDate,
          isActive: this.warrantyData.isActive
        }
        this.warrantyList = [ warranty ];
      }
      else if(type === 'sn'){
        const serialNumber = this.route.snapshot.paramMap.get('serialNumber');
        const sku = this.route.snapshot.paramMap.get('sku');

        if (!companyIdentifier || !serialNumber) {
          throw new Error('Missing required route parameters');
        }

        const payload: ItemDigitalPassportRequest = {
          serialNumber: serialNumber,
          sku: sku,
          companyIdentifier: companyIdentifier,
          securityPasscode: securityPasscode
        };

        // 2.
        this.itemData = await firstValueFrom(
          this.digitalPassportService.apiDigitalPassportSerialNumberPost(payload)
        );

        this.productInfo = {
          productName: this.itemData.productName,
          serialNumber: this.itemData.serialNumber,
          sku: this.itemData.sku,
          productImageUrl: this.itemData.productImageUrl,
          purchaseDate: this.itemData.purchaseDate,
          ctaUrl: this.itemData.ctaUrl
        };

        this.warrantyList = this.itemData.warranties || [];
      }
      else {
        throw new Error('Unkown url type');
      }

      this.displayPasscodeDialog = false;
      const notices = await firstValueFrom(this.digitalPassportService.apiDigitalPassportNoticesValueGet(companyIdentifier));

      const headerNotice = notices.find(n => n.placement === NoticePlacement[NoticePlacement.DIGITAL_PASSPORT_HEADER]);
      if (headerNotice) {
        this.headerText = this.parseWysiwygContent(
          headerNotice.noticeTranslations?.find(x => x.isoCode === 'en')?.translation || null
        );
      }

     const footerNotice = notices.find(n => n.placement === NoticePlacement[NoticePlacement.DIGITAL_PASSPORT_FOOTER]);
    if (footerNotice) {
        this.footerText = this.parseWysiwygContent(
          footerNotice.noticeTranslations?.find(x => x.isoCode === 'en')?.translation || null
        );
      }

    } catch (err) {
      console.error('Error fetching digital passport:', err);

      if (this.displayPasscodeDialog) {
        this.error = this.translate.instant('DIGITAL_PASSPORT.PASSCODE.VERIFICATION_FAILED');
      } else {
        this.noWarrantyFound = true;
      }
    } finally {
      this.loading = false;
    }
  }

  async backToMainPage(): Promise<void> {
    const isAuthenticated = await this.sessionService.isLogged();

    if (!isAuthenticated) {
      this.router.navigate(['/login']);
      return;
    }

    const path = await this.loginNavigationService.resolveLandingPage();
    await this.loginNavigationService.navigateLandingPage(path);
  }

  private parseWysiwygContent(rawValue: string | null): string | null {
    console.log('Raw value:', rawValue);
    console.log('Raw value JSON:', JSON.stringify(rawValue));
    if (rawValue == null) {
      return null;
    }

    try {
      let content = JSON.parse(rawValue);
      if (typeof content === 'string') {
            content = JSON.parse(content);
      }
      if (!content || !Array.isArray(content.ops)) {
        console.error('Invalid notice content: expected a Quill JSON.');
        return rawValue;
      }

      return content;
    } catch (error) {
      console.error('Error parsing notice content:', error);
      return rawValue;
    }
  }
}
